import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpStatusCode } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, of, throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { AuthenticationService } from '@digitalforest/ngx-oak-api';
import { AuthService } from './auth.service';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  private authService = inject(AuthService);
  private authenticationService = inject(AuthenticationService);
  private router = inject(Router);
  private isRefreshing = false;

  private refreshToken(): Observable<string> {
    if (this.isRefreshing) {
      return throwError(() => new Error('Token refresh already in progress'));
    }

    const refreshToken = this.authService.getRefreshToken();
    if (!refreshToken) {
      console.debug('[AuthInterceptor] No refresh token available, redirecting to login');
      this.authService.removeTokens();
      this.router.navigate(['/login']);
      return throwError(() => new Error('No refresh token available'));
    }

    this.isRefreshing = true;
    console.debug('[AuthInterceptor] Refreshing token...');

    return this.authenticationService.refreshAccessToken({ refresh_token: refreshToken }).pipe(
      switchMap((response) => {
        this.isRefreshing = false;
        console.debug('[AuthInterceptor] Token refresh successful');

        // Update tokens in localStorage
        this.authService.setToken(response.access_token);
        this.authService.setRefreshToken(response.refresh_token);

        return of(response.access_token);
      }),
      catchError((error) => {
        this.isRefreshing = false;
        console.debug('[AuthInterceptor] Token refresh failed, redirecting to login');
        this.authService.removeTokens();
        this.router.navigate(['/login']);
        return throwError(() => error);
      }),
    );
  }

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    // Skip auth check for login and logout endpoints
    const isAuthEndpoint = request.url.includes('/login') || request.url.includes('/logout');
    const isRefreshEndpoint = request.url.endsWith('/refresh');

    const token = this.authService.getToken();

    if (token && !isAuthEndpoint) {
      if (isRefreshEndpoint) {
        // For refresh endpoint, use refresh token
        const refreshToken = this.authService.getRefreshToken();
        if (refreshToken) {
          request = request.clone({
            setHeaders: {
              Authorization: `Bearer ${refreshToken}`,
            },
          });
        }
      } else {
        // Check if token is expired before sending the request
        if (this.authService.isTokenExpired()) {
          console.debug('[AuthInterceptor] Token is expired, attempting refresh...');
          const refreshToken = this.authService.getRefreshToken();
          if (refreshToken && !this.isRefreshing) {
            return this.refreshToken().pipe(
              switchMap((newAccessToken) => {
                // Retry original request with new token
                const newRequest = request.clone({
                  setHeaders: {
                    Authorization: `Bearer ${newAccessToken}`,
                  },
                });
                return next.handle(newRequest);
              }),
              catchError((error) => {
                return throwError(() => error);
              }),
            );
          } else {
            console.debug('[AuthInterceptor] No refresh token or already refreshing, redirecting to login');
            this.authService.removeTokens();
            this.router.navigate(['/login']);
            return throwError(() => new Error('Token expired'));
          }
        }

        request = request.clone({
          setHeaders: {
            Authorization: `Bearer ${token}`,
          },
        });
      }
    }

    return next.handle(request).pipe(
      catchError((error) => {
        // Handle 401 Unauthorized - token is invalid/expired
        if (error.status === HttpStatusCode.Unauthorized) {
          console.debug('[AuthInterceptor] Received 401 response, redirecting to login');
          this.authService.removeTokens();
          this.router.navigate(['/login']);
        }

        return throwError(() => error);
      }),
    );
  }
}
